import {
  assistantChatPreview,
  bookingPreview,
  eventsPreview,
  floorsPreview,
  officeBuilderPreview,
  profilePreview,
  statsPreview,
  suppliesAndSuggestionsPreview,
} from '@/features/onboarding/components/previews';
import type { OnboardingChapter } from '@/features/onboarding/types';

const MCP_SERVER_URL = 'https://mcp.robinhoot.space/';

export const welcomeTour: OnboardingChapter = {
  id: 'welcome',
  label: 'Welcome tour',
  slides: [
    {
      id: 'intro',
      title: 'Welcome to RobinHoot',
      description:
        "Your hub for office days: **book a spot**, **see who's coming in**, and keep the good vibes going. Here's a quick tour of the essentials.",
      icon: 'Sparkles',
      image: 'logo',
    },
    {
      id: 'workspace',
      title: 'Workspace management',
      description:
        'Lay out each office in the **office builder**: place desks, meeting rooms and walls, mark printers, kitchens and exits, and save the layout so everyone can book from the map. Office settings turn **events**, **treats champions**, **guest bookings** and **facilities emails** on or off, and can set the **supplies order email**.',
      icon: 'Building2',
      preview: officeBuilderPreview,
      audience: ['admin'],
    },
    {
      id: 'book',
      title: 'Book your spot',
      description:
        'Pick a desk or meeting room on the map, choose a **time slot** or a **quick duration**, or book **office presence** without a specific desk. Booking for a **guest**? Add their email to show their Gravatar, and colleagues will see the booking is **by you**.',
      icon: 'CalendarCheck',
      preview: bookingPreview,
    },
    {
      id: 'floors',
      title: 'More than one floor',
      description:
        "An office can have several **floors**, and each one has its own **map**, **desks** and **meeting rooms**. Switch floors to see who's in and book a spot on the one you're visiting.",
      icon: 'Layers',
      preview: floorsPreview,
    },
    {
      id: 'events',
      title: 'Events and treats',
      description:
        "Spot **office events** on the calendar, and volunteer as **treats champion** to bring treats for everyone who's in that day.",
      icon: 'Cookie',
      preview: eventsPreview,
    },
    {
      id: 'supplies-suggestions',
      title: 'Supplies and suggestions',
      description:
        'Open the **clipboard** next to the office name on the booking page to ask for **water** in one tap or request any item with a **store link**. Send a **suggestion** to admins and office managers.',
      icon: 'ClipboardList',
      preview: suppliesAndSuggestionsPreview,
    },
    {
      id: 'profile',
      title: 'Set up your profile',
      description:
        "In your profile settings, pick which **notifications** you get, from booking reminders to new events. Working remotely? Turn on **Remote Worker** to let facilities know when you're coming in. And add your **dietary needs** and **favourite treats** so the treats champion knows what to bring.",
      icon: 'User',
      preview: profilePreview,
    },
    {
      id: 'stats',
      title: 'Explore office stats',
      description:
        'See how each office is used: its **busiest days**, **occupancy rate** and **top bookers**, plus **treats champions** and the most used **meeting rooms**. Pick any **date range**, with or without weekends.',
      icon: 'PieChart',
      preview: statsPreview,
    },
    {
      id: 'assistant',
      title: 'Book with your AI assistant',
      description: `Add ${MCP_SERVER_URL} as an **MCP server** in Claude, Cursor or another AI assistant, and sign in with your Hootsuite Google account. Then ask it to **find a free desk**, **manage your bookings**, or **check what's on** at the office.`,
      icon: 'Bot',
      preview: assistantChatPreview,
      copy: { label: 'Copy server URL', text: MCP_SERVER_URL },
    },
  ],
};
